import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { fromEvent } from 'file-selector';
import { ClassAttributes, createElement, DragEvent, Fragment, memo, SyntheticEvent } from 'react';
import Dropzone from 'react-dropzone';
import { FormattedMessage } from 'react-intl';

import UploadingFile from '../../../entity/UploadingFile';
import theme from '../../../theme';
import LfToBr from '../primitive/LfToBr';

type Props = ClassAttributes<HTMLElement> & {
  accept?: string;
  onSelectFiles: (e: SyntheticEvent<HTMLElement>, files: UploadingFile[]) => void;
  iconWidth?: number;
  disabled?: boolean;
  className?: string;
};

// eslint-disable-next-line react/display-name
const FileUploader = memo(
  ({ onSelectFiles, iconWidth, accept, className, disabled = false }: Props) => {
    const isSupportedFileSystemApi =
      typeof (window as any).FileSystemDirectoryEntry === 'function' ||
      typeof (window as any).requestFileSystem === 'function' ||
      typeof (window as any).webkitRequestFileSystem === 'function';

    const onDragAccepted =
      disabled === false ? (files: any, e: DragEvent<HTMLDivElement>) => onSelectFiles(e, files) : () => {};

    const Root = ({ children }: { children: React.ReactNode }) =>
      isSupportedFileSystemApi ? (
        <_Root
          disabled={disabled}
          accept={accept}
          onDropAccepted={onDragAccepted}
          getDataTransferItems={(e: Event) => fromEvent(e)}
          className={className}
        >
          {children}
        </_Root>
      ) : (
        <_Root disabled={disabled} accept={accept} onDropAccepted={onDragAccepted} className={className}>
          {children}
        </_Root>
      );

    return (
      <Root>
        {({ isDragActive }: { isDragActive: boolean }) => {
          if (isDragActive) {
            return (
              <>
                <UploaderIcon width={iconWidth != null ? iconWidth : 50} src="/assets/asset_form_uploader.svg" />
                <DescriptionInDragging variant="caption" align="center">
                  <FormattedMessage id="file_uploader.description_when_dragover" />
                </DescriptionInDragging>
              </>
            );
          } else {
            return (
              <>
                <UploaderIcon
                  width={iconWidth != null ? iconWidth : 50}
                  src={disabled ? '/assets/asset_form_uploader_disable.svg' : '/assets/asset_form_uploader.svg'}
                />

                <StandbyDescription variant="caption" align="center">
                  <FormattedMessage id="file_uploader.description">
                    {(label: string) => <LfToBr text={label} />}
                  </FormattedMessage>
                </StandbyDescription>
              </>
            );
          }
        }}
      </Root>
    );
  },
  (prevProps, nextProps) => prevProps.accept === nextProps.accept && prevProps.disabled === nextProps.disabled,
);

const _Root = styled(Dropzone)<{ disabled: boolean }>((props) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridTemplateRows: 'auto auto',
  gridTemplateAreas: "'icon' 'description'",
  alignItems: 'center',
  justifyItems: 'center',
  width: '100%',
  height: '100%',
  padding: 24,
  border: props.disabled ? '1px #BDBDBD solid' : '2px #DFDFDF solid',
  borderRadius: 4,
  cursor: 'pointer',
}));

const descriptionStyle = {
  gridArea: 'description',
  height: 48,
};

const UploaderIcon = styled('img')<{ width: number }>`
  width: ${(props) => props.width}%;
`;

const StandbyDescription = styled(Typography)({
  ...descriptionStyle,
  [theme.breakpoints.down('md')]: {
    height: 'auto',
    whiteSpace: 'pre-wrap',
  },
});

const DescriptionInDragging = styled(Typography)({
  ...descriptionStyle,
  [theme.breakpoints.down('md')]: {
    height: 'auto',
    whiteSpace: 'pre-wrap',
  },
});

export default FileUploader;
